import React from "react";
import "react-toastify/dist/ReactToastify.css";
import DataTable, { createTheme } from "react-data-table-component";
import { useTheme } from "../../../context/ThemeContext";
import TableLoader from "../../skeletons/TableLoader";
import { checkPermission } from "../../../helpers/GeneralHelper";
import { utcToLocal } from "../../../helpers/DateHelper";
import { getCmsDataTableStyles } from "../../../helpers/DataTableStyles";

export default function CandidateTable({
  module,
  data,
  loading,
  totalRows,
  page,
  limit,
  fetchData,
  openUpdateModal,
  openDeleteModal,
  handleView,
  isTrash,
}) {

  const columns = [
    {
      name: "#",
      selector: (row, index) => <div>{ ((page -1 ) * limit) + index +1 }.</div>,
      width: '70px',
      center: true,
    },
    {
      name: "Candidate Name",
      selector: (row) => row.candidate_firstname + ' ' + row.candidate_lastname,
      sortable: true,
      wrap: true,
      minWidth: "150px",
      grow: 2,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "Client",
      selector: (row) => row.client_name,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "Requirement",
      selector: (row) => row.requirement_name,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "Mobile",
      selector: (row) => row.candidate_mobile,
      sortable: true, // ✅ enable sorting
    },
    {
      name: "Email",
      selector: (row) => row.candidate_email,
      sortable: true, // ✅ enable sorting
      cell: (row) => {
        return (
          <a href={`mailto:${row.candidate_email}`} className="text-primary">
            {row.candidate_email}
          </a>
        );
      },
    },
    {
      name: "Recruiter",
      selector: (row) => row.recruiter_name,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "Status",
      selector: (row) => row.status_name,
      sortable: true, // ✅ enable sorting
      cell: (row) => {
        let btnClass = "badge bg-warning text-black"; // default

        if (row.status_name?.toLowerCase().includes("select")) {
          btnClass = "badge bg-success";
        } else if (row.status_name?.toLowerCase().includes("reject")) {
          btnClass = "badge bg-danger";
        }else if (row.status_name?.toLowerCase().includes("hold")) {
          btnClass = "badge bg-secondary";
        }else if (row.status_name?.toLowerCase().includes("started")) {
          btnClass = "badge bg-info";
        }

        return (
          <span className={`${btnClass} badge-wrap`}>
            {row.status_name}
          </span>
        );
      },
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: 'none',
      },
      wrap: true,
    },
    {
      name: "Last Updated",
      selector: (row) => row.candidate_update_time ? (row.candidate_update_time) : (row.candidate_create_time), // keep sortable raw value
      sortable: true,
      cell: (row) => {
        var updatedAt;
        if (!row.candidate_update_time) {
          //return <span className="text-muted">N/A</span>;
          updatedAt = new Date(row.candidate_create_time);
        }else{
          updatedAt = new Date(row.candidate_update_time);
        }

        const now = new Date();
        const diffMs = now - updatedAt; // milliseconds difference

        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        let display = "";
        if (diffDays > 0) {
          display = `${diffDays}d ${diffHours % 24}h`;
        } else if (diffHours > 0) {
          display = `${diffHours}h ${diffMins % 60}m`;
        } else {
          display = `${diffMins}m`;
        }

        return <span className="badge bg-primary">{display} ago</span>;
      },
    },

    // {
    //   name: "Added On",
    //   selector: (row) => utcToLocal(row.candidate_create_time)  
    // },
    // {
    //   name: "Last Edited On",
    //   selector: (row) => utcToLocal(row.candidate_update_time)
    // },
  ];

  if (! isTrash && (checkPermission(module.route, 'edit') || checkPermission(module.route, 'delete'))) {
    columns.push({
      name: "Action",
      right: "true",
      cell: row => <div className="hstack gap-2 fs-15">
                    {checkPermission(module.route, 'edit') && <button type="button" className="btn btn-icon btn-sm btn-info" title="Edit" onClick={() => openUpdateModal(row)}>
                      <i className='bx bxs-edit'></i>
                    </button>}

                    {checkPermission(module.route, 'delete') && <button type="button" className="btn btn-icon btn-sm btn-danger" title="Delete" onClick={() => openDeleteModal(row)}>
                      <i className='bx bxs-trash'></i>
                    </button>}
                  </div>
    });
  }

  if (isTrash) {
    columns.push({
      name: "Deleted At",
      selector: (row) => utcToLocal(row.candidate_delete_time)
    });
  }

  const { theme, toggleTheme } = useTheme();

  const handlePageChange = async (page) => {
    fetchData(limit, page);
  };

  const handleLimitChange = async (newLimit, page) => {
    //if ((newLimit !== 10 && page !== 1) || localStorage.getItem(module.key)) {
      fetchData(newLimit, page);
    //}
  };

  // useEffect(() => {
  //   fetchData(limit, page);
  // }, []);

  createTheme("dark", {
    text: {
      primary: "#a2a3a4",
      secondary: "#2aa198",
    },
    background: {
      default: "#252729",
    },
    context: {
      background: "#cb4b16",
      text: "#a2a3a4",
    },
    divider: {
      default: "#073642",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  });

  return (
    <DataTable
      // title={`Manage Notes (Private)`}
      className="clickable cms-data-table"
      onRowClicked={(row) => handleView(row)}
      columns={columns}
      data={data}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      onChangeRowsPerPage={handleLimitChange}
      onChangePage={handlePageChange}
      progressPending={loading}
      progressComponent={<TableLoader noOfColumn={columns.length} />}
      fixedHeader
      fixedHeaderScrollHeight="550px"
      highlightOnHover
      paginationPerPage={10}  // default page size
      paginationRowsPerPageOptions={[10, 25, 50, 100, totalRows]} // 👈 added custom options
      theme={`${theme === "light" ? "light" : "dark"}`}
      customStyles={getCmsDataTableStyles(theme)}
    />
  );
};
